import {
  SiteResponse,
  Site,
  RetailSite,
  ShowroomSite,
  DistributionSite,
  CorporateSite,
} from '../interfaces/Site';
import Geo from '../interfaces/Geo';
import SiteType from '../interfaces/SiteType';
import BusinessStatus from '../interfaces/BusinessStatus';

function isValidSite(site: any): site is Site {
  return (
    'SiteId' in site &&
    'SiteType' in site &&
    Object.values(SiteType).includes(site.SiteType) &&
    'Geo' in site &&
    Object.values(Geo).includes(site.Geo) &&
    'Address' in site
  );
}

const getSiteCode = (site: SiteResponse): string => {
  const siteNumberFromId: string = site.SiteId.substring(3, 7);
  const convertToNumber = Number(siteNumberFromId);

  if (!isNaN(convertToNumber)) {
    return convertToNumber.toString();
  } else {
    return siteNumberFromId;
  }
};

export default function siteResponseToSiteObjConverter(response: SiteResponse[]) {
  const nonValidSites: any[] = [];
  const validSites: Site[] = [];
  const errors: string[] = [];
  const allSiteTypes = new Set<SiteType>();

  response.forEach((site, index) => {
    try {
      if (!isValidSite(site)) {
        nonValidSites.push(site);
        throw new Error(`Invalid site for ${site.SiteId}`);
      }

      const address = Object.values(site.Address)
        .filter(value => value.length > 0)
        .join('|');

      const searchProperties = [
        site.SiteName,
        site.SiteType,
        site.Brand,
        site.Geo,
        site.SiteId,
        address,
        getSiteCode(site),
      ];

      if (site.SiteType === SiteType.Retail) {
        searchProperties.push(site.BusinessStatus);
        searchProperties.push(site.StoreConcept);
      }

      const siteSearch = searchProperties.join('|').toLowerCase();

      allSiteTypes.add(site.SiteType as SiteType);

      const siteObj = {
        ...site,
        id: site.SiteId,
        brand: site.Brand,
        geo: site.Geo as Geo,
        siteId: site.SiteId,
        siteCode: getSiteCode(site),
        siteType: site.SiteType as SiteType,
        address: site.Address,
        siteSearch: siteSearch,
        siteName: site.SiteName,
      };

      switch (site.SiteType as SiteType) {
        case SiteType.Retail:
          validSites.push({
            ...siteObj,
            businessStatus: site.BusinessStatus as BusinessStatus,
            hasNpos: site.HasNpos,
            storeConcept: site.StoreConcept,
          } as RetailSite);
          break;
        case SiteType.Showroom:
          validSites.push({
            ...siteObj,
          } as ShowroomSite);
          break;
        case SiteType.Distribution:
          validSites.push({
            ...siteObj,
          } as DistributionSite);
          break;
        case SiteType.Corporate:
          validSites.push({
            ...siteObj,
          } as CorporateSite);
          break;
        case SiteType.Headquarters:
          validSites.push({
            ...siteObj,
          } as CorporateSite);
          break;
        case SiteType.Lab:
          validSites.push({
            ...siteObj,
          } as CorporateSite);
          break;
        case SiteType.DataCenter:
          validSites.push({
            ...siteObj,
          } as CorporateSite);
          break;
        case SiteType.Office:
          validSites.push({
            ...siteObj,
          } as CorporateSite);
          break;
        case SiteType.Gwan:
          validSites.push({
            ...siteObj,
          } as CorporateSite);
          break;
        default:
          throw new Error(`Unknown site type: ${site.siteType} for ${site.siteId}`);
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error(error.message);
        errors.push(error.message);
      } else {
        console.error(String(error));
        errors.push(String(error));
      }
      nonValidSites.push(site);
    }
  });

  return {validSites, nonValidSites, errors, allSiteTypes};
}
